<template>
  <div class="section">
    <section-header v-if="!hideTitle"
                    :ressource="ressource"
                    :isExtended="extended"
                    :dialog="dialog"
                    @extended=setExtended
                    @closeDialog=closeDialog
                    ref="CONTRAT">
    </section-header>

    <div v-if="extended">
      <div :class="dialog ? 'p-5' : 'pt-2 pb-5'">
        <contrat-template-select v-if="canSelectContratTemplate"
                                 ref="CONTRAT_TEMPLATE_ID"
                                 :value="editContrat.contratTemplateId"
                                 :ressource="ressourceContratTemplate"
                                 :required="true"
                                 :contextId="editContrat.id"
                                 :readOnly="templateReadOnly"
                                 @update:value="selectContratTemplate">
        </contrat-template-select>

        <section-valuate-meta-attrib ref="VALUATE_META_ATTRIB"
                                     id="VALUATE_META_ATTRIB"
                                     :hideTitle="true"
                                     :extended="true"
                                     :showButton="false"
                                     :disabled="metaDisabled"
                                     :dialog="false"
                                     :metaAttribs="metaAttrib"
                                     @metaAttribUpdated="metaAttribUpdated">
        </section-valuate-meta-attrib>

        <edit-field v-if="!dialog" ref="EDIT_DIALOG"
                    :data-table=false
                    :edit-field-info=editFieldInfo
                    :disabled=true
                    :value=codeCheque
                    @error=setError
                    @updated=setUpdated>
        </edit-field>

      </div>
      <div :class="dialog ? 'p-5' : 'pt-2 pb-5'">
        <div :class="getClass()" v-html="getContratInfo()"></div>
      </div>
      <div class="d-flex flex-row-reverse">
        <div v-for="(state, index) in contratState.actions" :key="index">
          <loader-button class="ms-2"
                         type="submit"
                         :label="state"
                         :outlined="outlined(index)"
                         @callback="callBackContrat(index)">
          </loader-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import CrudContrat from '@/generated/cruds/CrudContrat';
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import State from '@/generated/classes/State';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import { notifClass } from '@/framework/base/helpers/Notif';
  import SectionValuateMetaAttrib from '@/framework/metaAttrib/components/SectionValuateMetaAttrib.vue';
  import MetaAttrib from '@/generated/classes/MetaAttrib';
  import { storeContrat, storeEtablissement, storeMetaAttrib } from '@/generated/stores';
  import { ContratStatuts } from '@/generated/classes/Contrat';
  import Contrat from '@/extend/classes/Contrat';
  import { defineComponent } from "vue";
  import EditField from "@/framework/base/components/EditField.vue";
  import {EditFieldType, FieldInfo} from "@/framework/base/components/FieldInfo.ts";
  import {MapRessource} from "@/framework/base/helpers/Ressources.ts";
  import ContratTemplateSelect from '@/generated/controls/contratTemplate/ContratTemplateSelect.vue';
  import Subscription from '@/generated/classes/Subscription';
  import SubscriptionType from '@/generated/classes/SubscriptionType';

  export default defineComponent({
    components: {
      EditField,
      SectionValuateMetaAttrib,
      ContratTemplateSelect
    },
    extends: CrudContrat,
    props: {
      hideTitle: {
        type: Boolean
      }
    },
    created () {
      this.loadCanSelectContratTemplate();
    },
    data () {
      return {
        lockStatutAction: false,
        contratActiveState: new State(),
        metaUpdated: false,
        templateSaving: false,
        canSelectContratTemplate: false
      };
    },
    computed: {
      codeCheque (): string {
        return this.contrat.codeCheque;
      },
      editFieldInfo (): FieldInfo {
        const ressource = new MapRessource();
        ressource.label = "Numéro du contrat de travail";
        const type = EditFieldType.INT;
        const precision = 0;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(ressource, type, precision, mask, format, focus, live);
      },
      ressource () {
        return RessourceGenSection(GenSection.CONTRAT);
      },
      ressourceContratTemplate (): MapRessource {
        return RessourceGenControl(GenControl.CONTRAT_CONTRAT_TEMPLATE_SELECT);
      },
      templateReadOnly (): boolean {
        return this.templateSaving ||
          (this.editContrat.statut >= ContratStatuts.SUBMITTED && this.editContrat.statut != ContratStatuts.MODIFIED);
      },
      contratState (): State {
        if (this.contratActiveState.objId != this.editContrat.id || this.contratActiveState.statut != this.editContrat.statut) {
          this.updateState();
          this.callBackContrat();
        }
        return this.contratActiveState;
      },
      metaDisabled (): boolean {
        return this.editContrat.statut >= ContratStatuts.SUBMITTED && this.editContrat.statut != ContratStatuts.MODIFIED;
      },
      metaAttrib (): MetaAttrib[] {
        const res: MetaAttrib[] = [];
        storeMetaAttrib.listMetaAttrib.forEach((metaAttrib) => {
          const clone = MetaAttrib.fromJson(metaAttrib);
          const metaValue = this.editContrat.metaValues.find(a => a.metaAttributId == metaAttrib.id);
          if (metaValue) {
            clone.value = metaValue.value;
          }
          clone.classeId = this.editContrat.id;
          res.push(clone);
        })
        return this.filterMetaAttrib(res);
      }
    },
    methods: {
      loadCanSelectContratTemplate (): void {
        const etablissement = storeEtablissement.getEtablissement(this.contrat.etablissementId);
        if (etablissement.subscriptionId <= 0) {
          return;
        }

        Subscription.get(etablissement.subscriptionId)
          .then((subscription: Subscription) => SubscriptionType.get(subscription.subscriptionTypeId))
          .then((subscriptionType: SubscriptionType) => {
            this.canSelectContratTemplate = subscriptionType.hasCustomContrat;
          })
          .catch(() => {
            this.canSelectContratTemplate = false;
          });
      },
      selectContratTemplate (templateId: number): void {
        if (this.templateSaving || templateId <= 0 || templateId == this.editContrat.contratTemplateId) {
          return;
        }

        const previousTemplateId = this.editContrat.contratTemplateId;
        this.editContrat.contratTemplateId = templateId;
        this.templateSaving = true;
        storeContrat.updateContrat(this.editContrat)
          .then((data: Contrat) => {
            if (!data.failed) {
              this.refContrat = storeContrat.getContrat(data.id);
              this.cloneContrat = data.cloneContrat();
            } else {
              this.editContrat.contratTemplateId = previousTemplateId;
            }
            this.templateSaving = false;
          })
          .catch(() => {
            this.editContrat.contratTemplateId = previousTemplateId;
            this.templateSaving = false;
          });
      },
      updateState () {
        this.contratActiveState.objType = ObjTypes.CONTRAT;
        this.contratActiveState.objId = this.editContrat.id;
        this.contratActiveState.statut = this.editContrat.statut;
      },
      getContratInfo (): string {
        return this.contratActiveState.info;
      },
      getClass (): string {
        return notifClass(this.contratActiveState.notifDefType);
      },
      outlined (index?: number) {
        let res = false;
        if (this.contratState.outlined && this.contratState.outlined.length >= index) {
          res = this.contratState.outlined[index];
        }
        return res;
      },
      callBackContrat (index?: number) {
        if (!this.lockStatutAction) {
          this.lockStatutAction = true;
          if (index != null) {
            this.contratState.actionIndex = index + 1;
          } else {
            this.contratState.actionIndex = 0;
          }
          if (this.metaUpdated) {
            MetaAttrib.valuate(this.metaAttrib).then(() => {
              State.process(this.contratState).then((res) => {
                this.contratActiveState = res;
                this.lockStatutAction = false;
              }).catch(() => {
                this.lockStatutAction = false;
              });
            });
          } else {
            State.process(this.contratState).then((res) => {
              this.contratActiveState = res;
              this.lockStatutAction = false;
            }).catch(() => {
              this.lockStatutAction = false;
            });
          }
        }
      },
      filterMetaAttrib (metaAttrib: MetaAttrib[]): MetaAttrib[] {
        const filterMetaAttrib = metaAttrib.filter(a => {
          return (
            a.classe == 'CONTRAT' &&
            (a.contratTemplateId == this.editContrat.contratTemplateId) &&
            (a.conventionIds.includes(this.editContrat.conventionAppliqueeId) || a.conventionIds.length == 0) &&
            (a.emploiIds.includes(this.editContrat.emploiId) || a.emploiIds.length == 0)
          )
        });
        filterMetaAttrib.forEach((meta: MetaAttrib) => {
          meta.docObjType = ObjTypes.CONTRAT;
          meta.docObjId = this.editContrat.id;
        });
        return filterMetaAttrib;
      },
      metaAttribUpdated (state: boolean) {
        this.metaUpdated = state;
      }
    }
  })

</script>

<style scoped>
  td {
    padding: 10px !important;
  }
</style>
